import api from './api';
import { IGrade, IGradeSelecao, IPadMer, IVariacao } from '../types/Grade';

// Grade do produto (cor x tamanho). A gravação vai junto com a ficha, na chave "grade" do POST/PUT /mercador.

export async function buscarGrade(cod: number | string): Promise<IGrade> {
  const response = await api.get('/grade', { params: { cod } });
  return {
    codbar: response.data?.codbar || '',
    cores: response.data?.cores || [],
    tamanhos: response.data?.tamanhos || [],
    variacoes: response.data?.variacoes || []
  };
}

// Cores cadastradas (GET genérico, que já filtra ativo)
export async function listarCores(): Promise<IPadMer[]> {
  const response = await api.get('/padmer', { params: { per_page: 500, order: 'padmer' } });
  // Com total=0 a API devolve {} (sem data)
  return (response.data?.data || []).map((cor: any) => ({
    cod: String(cor.cod ?? '').trim(),
    padmer: String(cor.padmer ?? '').trim()
  }));
}

export const chaveVariacao = (codpad: string, codtam: string) => `${codpad.trim()}\t${codtam.trim()}`;

export interface IPlanoGrade {
  /** Combinações, na ordem da API: tamanho por fora, cor por dentro */
  combinacoes: { codpad: string; codtam: string }[];
  /** Combinação → variação gravada que a representa */
  existentes: Map<string, IVariacao>;
  novas: Set<string>;
  removidas: IVariacao[];
}

// Mesma regra da API (uGradeMercador.SincronizaGrade), para mostrar o que vai acontecer ao salvar:
// a variação com cor ou tamanho vazio assume uma combinação nova compatível em vez de ser removida.
export function planejarGrade(variacoes: IVariacao[], selecao: IGradeSelecao): IPlanoGrade {
  const cores = selecao.cores.length ? selecao.cores : [''];
  const tamanhos = selecao.tamanhos.length ? selecao.tamanhos : [''];
  const combinacoes = tamanhos.flatMap(codtam => cores.map(codpad => ({ codpad, codtam })));
  const chaves = combinacoes.map(c => chaveVariacao(c.codpad, c.codtam));

  // Principal primeiro, como na API
  const ordenadas = [...variacoes].sort((a, b) => (a.filtro === 0 ? -1 : 0) - (b.filtro === 0 ? -1 : 0));
  const existentes = new Map<string, IVariacao>();
  ordenadas.forEach(v => {
    const chave = chaveVariacao(v.codpad, v.codtam);
    if (chaves.includes(chave) && !existentes.has(chave)) existentes.set(chave, v);
  });

  const removidas: IVariacao[] = [];
  ordenadas.forEach(v => {
    if (chaves.includes(chaveVariacao(v.codpad, v.codtam))) return;
    const pad = v.codpad.trim();
    const tam = v.codtam.trim();
    const compativel = (pad === '' || tam === '') && combinacoes.find(c =>
      !existentes.has(chaveVariacao(c.codpad, c.codtam)) &&
      (pad === '' || pad === c.codpad) && (tam === '' || tam === c.codtam));
    if (compativel) existentes.set(chaveVariacao(compativel.codpad, compativel.codtam), v);
    else removidas.push(v);
  });

  const novas = new Set(chaves.filter(chave => !existentes.has(chave)));
  return { combinacoes, existentes, novas, removidas };
}
